import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import type { Discrepancy } from "../api/types";

/**
 * The script's own marks beside a chapter's flags (server: flag_notes).
 *
 * Each mark comes with the manuscript tokens it is near; a flag whose words
 * are among them shows the mark on its card. Read once per book in the
 * background, so the first answer for a book may be "checking".
 */

export type ScriptMarkKind = "note" | "comment" | "struck" | "highlight" | "ink";

export interface ScriptMark {
  id: string;
  kind: ScriptMarkKind;
  page: number;      // as the report shows pages
  pdf_page: number;  // the PDF's own page, for #page=N
  color: string;
  near: [number, number][];
  at: number;        // the word its margin note lines up with (manuscript panel)
  where?: "text" | "margin";   // ink: drawn over the text, or in a margin
  text?: string;
  parts?: { who: string; text: string }[];   // a comment thread
}

export interface ScriptNotes {
  state: "none" | "checking" | "failed" | "ready";
  marks: ScriptMark[];
}

export interface ScriptSnippet {
  page: number;
  pdf_page: number;
  image: string;
  aspect: number;
  rings: [number, number, number, number][];   // left, top, width, height - fractions of the picture
  marks: string[];
}

export function useScriptNotes(projectId: string | undefined, chapterId: string | undefined) {
  return useQuery({
    queryKey: ["script-notes", projectId, chapterId],
    queryFn: () => api<ScriptNotes>(`/api/modules/proofing/projects/${projectId}/chapters/${chapterId}/script-notes`),
    refetchInterval: (q) => (q.state.data?.state === "checking" ? 8000 : false),
    staleTime: 5 * 60_000,
    enabled: !!projectId && !!chapterId,
  });
}

/** The manuscript words a flag is about. An "added" flag has none of its own:
 *  it sits before a word, so it is about where that word begins. */
export function flagTokens(d: Pick<Discrepancy, "manuscript_token_indices" | "insert_before_index">): number[] {
  if (d.manuscript_token_indices?.length) return d.manuscript_token_indices;
  const at = d.insert_before_index;
  return at == null ? [] : [Math.max(0, at - 1), at];
}

// Which mark leads a card: words the script itself says, then where it says them.
const RANK = (m: ScriptMark) =>
  m.kind === "note" ? 0 : m.kind === "comment" ? 1 : m.kind === "struck" ? 2
  : m.kind === "ink" && m.where === "margin" ? 3 : m.kind === "highlight" ? 4 : 5;

/** Each flag's marks, leading mark first. Flags with none are left out. */
export function marksByFlag(
  flags: { d: Pick<Discrepancy, "id" | "manuscript_token_indices" | "insert_before_index"> }[],
  marks: ScriptMark[] | undefined,
): Map<string, ScriptMark[]> {
  const out = new Map<string, ScriptMark[]>();
  if (!marks?.length) return out;
  for (const { d } of flags) {
    const toks = flagTokens(d);
    if (!toks.length) continue;
    const hit = marks.filter((m) => m.near.some(([lo, hi]) => toks.some((t) => t >= lo && t <= hi)));
    if (hit.length) out.set(d.id, hit.sort((a, b) => RANK(a) - RANK(b)));
  }
  return out;
}

/** How a card names a mark. */
export function markLabel(m: ScriptMark): string {
  if (m.kind === "note") return "Script note";
  if (m.kind === "comment") return "Editor comment";
  if (m.kind === "struck") return "Struck in the script";
  if (m.kind === "highlight") return "Highlighted in the script";
  return "Marked in the script";
}

/** One line saying what the mark is - its words when it has them. */
export function markGist(m: ScriptMark): string {
  if (m.kind === "comment") return m.parts?.[0]?.text || m.text || "";
  if (m.kind === "note" || m.kind === "struck") return m.text || "";
  if (m.kind === "highlight") return "these words are highlighted";
  return m.where === "margin" ? "handwriting beside these words" : "a mark drawn on these words";
}
